// src/pages/LandingPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom'; // ✅ Import navigation hook
import './LandingPage.css';
import earthVideo from '../assets/earth-loop.mp4';
import AnimatedLogo from '../components/AnimatedLogo';

export default function LandingPage() {
  const navigate = useNavigate(); // ✅ Create navigate instance

  const handleGetStarted = () => {
    navigate('/login'); // ✅ Navigate to login page
  };

  useEffect(() => {
  const videoEl = document.querySelector('.bg-video');
  if (videoEl) {
      videoEl.playbackRate = 0.8;
          }
        }, []);

  return (
    <div className="landing-root">
      {/* HERO */}
      <section className="hero-section">
         <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: 9999,
              padding: '8px',
              width: 180,
              height: 180,
              maxWidth: '180px',   
              maxHeight: '180px',   
            }}
          >
            <AnimatedLogo size={180} className="fixed top-0 left-0 z-[9999] p-2" />
          </div>
        <video className="bg-video" autoPlay muted loop playsInline>
          <source src={earthVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="bg-overlay" />
        <motion.div
          className="glass-box"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.h1 className="hero-title">Smart Data. Fast Action.</motion.h1>
          <motion.p className="hero-subtitle">
            Automate reporting. Unify dashboards. See insights in real-time.
          </motion.p>
          <motion.button
            className="hero-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleGetStarted} // ✅ Add click handler
          >
            Get Started
          </motion.button>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section className="features-section">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Why Choose Us?
        </motion.h2>
        <div className="features-grid">
          {[
            { title: 'Smart Triggers', desc: 'Automate tasks based on behavior and timing.' },
            { title: 'Real-Time Analytics', desc: 'View real-time performance insights and KPIs.' },
            { title: 'No-Code Builder', desc: 'Build powerful workflows without writing a line of code.' },
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              className="feature-card"
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true, amount: 0.4 }}
            >
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-it-works">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          How It Works
        </motion.h2>
        <div className="steps">
          {[
            'Define your workflow goal',
            'Use drag-and-drop builder to design',
            'Deploy and monitor performance',
          ].map((step, index) => (
            <motion.div
              key={index}
              className="step"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <span>{index + 1}</span>
              <p>{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          What Our Clients Say
        </motion.h2>
        <motion.blockquote
          className="quote"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <p>“We automated 80% of our operations without writing a single line of code. This is the future.”</p>
          <footer>– Ameen H., Operations Director</footer>
        </motion.blockquote>
      </section>

      {/* CTA FOOTER */}
      <section className="footer-cta">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, bounce: 0.3 }}
          viewport={{ once: true }}
        >
          <h2>Start Automating Today</h2>
          <p>Experience the power of AI workflows tailored to your business.</p>
          <button>Try It Free</button>
        </motion.div>
      </section>
    </div>
  );
}
